import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Map, Users, ChevronRight } from "lucide-react";
import MaintenanceLayout from "../shared/MaintenanceLayout";
import { listSettlementOrgs, OrgSummary } from "../../../api/settlement";
import { useNotice } from "../shared/useNotice";

const card: React.CSSProperties = {
  background: "#fff",
  border: "1px solid #e0e0e0",
  borderRadius: 10,
  padding: 16,
  marginBottom: 16,
};

const pillBase: React.CSSProperties = {
  display: "inline-block",
  padding: "2px 9px",
  borderRadius: 999,
  fontSize: 12,
  fontWeight: 600,
};

const quickLinks: {
  to: string;
  title: string;
  desc: string;
  Icon: React.ComponentType<{ size?: number; color?: string }>;
}[] = [
  {
    to: "/maintenance/settlement/coverage",
    title: "Coverage",
    desc: "Find priced configs with no settlement profile.",
    Icon: Map,
  },
  {
    to: "/maintenance/settlement/parties",
    title: "Parties",
    desc: "Directory of entities that pay or receive money.",
    Icon: Users,
  },
];

const SettlementOverview: React.FC = () => {
  const [orgs, setOrgs] = useState<OrgSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useNotice("error", "");

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    listSettlementOrgs()
      .then((data) => {
        if (active) setOrgs(data);
      })
      .catch((err) => {
        console.error("Failed to load settlement orgs:", err);
        if (active) setError("Failed to load organisations");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const totalPriced = orgs.reduce((sum, o) => sum + o.priced_configs, 0);
  const totalConfigured = orgs.reduce((sum, o) => sum + o.configured, 0);

  return (
    <MaintenanceLayout title="Settlements">
      {/* Quick links */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
          gap: 16,
          marginBottom: 16,
        }}
      >
        {quickLinks.map(({ to, title, desc, Icon }) => (
          <Link
            key={to}
            to={to}
            style={{ ...card, marginBottom: 0, display: "block", textDecoration: "none", color: "inherit" }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
              <Icon size={20} color="#2563eb" />
              <span style={{ fontSize: 16, fontWeight: 600, color: "#333" }}>{title}</span>
              <ChevronRight size={16} color="#777" style={{ marginLeft: "auto" }} />
            </div>
            <div style={{ fontSize: 13, color: "#777" }}>{desc}</div>
          </Link>
        ))}
      </div>

      {/* Organisations table */}
      <div style={card}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 12,
          }}
        >
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: "#333" }}>
            Settlement by organisation
          </h3>
          {!loading && !error && (
            <span style={{ fontSize: 13, color: "#777" }}>
              {orgs.length} organisations · {totalConfigured}/{totalPriced} priced configs
              configured
            </span>
          )}
        </div>

        {loading ? (
          <p style={{ color: "#777", margin: 0 }}>Loading…</p>
        ) : error ? (
          <p style={{ color: "#c0392b", margin: 0 }}>{error}</p>
        ) : orgs.length === 0 ? (
          <p style={{ color: "#777", margin: 0 }}>No organisations found.</p>
        ) : (
          <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 14 }}>
            <thead>
              <tr>
                <th style={th}>Organisation</th>
                <th style={{ ...th, textAlign: "right" }}>Priced configs</th>
                <th style={{ ...th, textAlign: "right" }}>Configured</th>
                <th style={th}>Status</th>
                <th style={th}></th>
              </tr>
            </thead>
            <tbody>
              {orgs.map((o) => {
                const allConfigured = o.configured === o.priced_configs;
                const unconfigured = o.priced_configs - o.configured;
                return (
                  <tr key={o.org_id}>
                    <td style={td}>
                      <Link
                        to={`/maintenance/settlement/orgs/${o.org_id}`}
                        style={{ color: "#2563eb", textDecoration: "none", fontWeight: 600 }}
                      >
                        {o.org_name}
                      </Link>
                    </td>
                    <td style={{ ...td, textAlign: "right" }}>{o.priced_configs}</td>
                    <td style={{ ...td, textAlign: "right" }}>{o.configured}</td>
                    <td style={td}>
                      {allConfigured ? (
                        <span style={{ ...pillBase, background: "#e8f7ef", color: "#1a7f4b" }}>
                          All configured
                        </span>
                      ) : (
                        <span style={{ ...pillBase, background: "#fff7e6", color: "#b9770e" }}>
                          {unconfigured} unconfigured
                        </span>
                      )}
                    </td>
                    <td style={td}>
                      <Link
                        to={`/maintenance/settlement/orgs/${o.org_id}/runs`}
                        style={{ color: "#2563eb", textDecoration: "none", fontWeight: 600 }}
                      >
                        Settlements →
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </MaintenanceLayout>
  );
};

const th: React.CSSProperties = {
  borderBottom: "1px solid #e0e0e0",
  padding: "8px 10px",
  textAlign: "left",
  fontSize: 12,
  fontWeight: 600,
  color: "#777",
  textTransform: "uppercase",
  letterSpacing: ".03em",
};

const td: React.CSSProperties = {
  borderBottom: "1px solid #f0f0f0",
  padding: "8px 10px",
  color: "#333",
};

export default SettlementOverview;
